import React, { useEffect } from 'react';
import { getItemWithExpiration, setItemWithExpiration } from '../lib/utils';
import { AuthOwner, captureAuthSession, isAuthSessionCurrent } from '../lib/authSessionGuard';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * 이용 상태 경고를 확인한 뒤 저장할 authObject. **기존 authObject 를 펼친 뒤** 확인 표시만 덮는다 —
 * 예전엔 필드 목록을 다시 만들어 정산 방식(settlementType)·부가세(vatPolicy·vatRate) 등이 빠졌다(웹 검증 2026-11-02).
 */
export const buildConfirmedUsableStatusAuthObject = (authObject: any) => ({
    ...authObject,
    adminId: authObject?.adminId,
    id: authObject?.id,
    name: authObject?.name,
    email: authObject?.email,
    gender: authObject?.gender,
    phoneNumber: authObject?.phoneNumber,
    profileImageUrl: authObject?.profileImageUrl,
    /** 센터 */
    centerAddress: authObject?.centerAddress,
    centerCategory: authObject?.centerCategory,
    centerCode: authObject?.centerCode,
    centerName: authObject?.centerName,
    centerId: authObject?.centerId,
    isNewCommunicationBox: authObject?.isNewCommunicationBox,

    /** 구독플랜관련 */
    coachCount: authObject?.coachCount,
    memberCount: authObject?.memberCount,
    activationDate: authObject?.activationDate,
    expirationDate: authObject?.expirationDate,
    expirationDelayDate: authObject?.expirationDelayDate,
    finalPaymentDate: authObject?.finalPaymentDate,
    finalPaymentTryDate: authObject?.finalPaymentTryDate,

    cardIssuingBank: authObject?.cardIssuingBank, // 결제카드 은행
    cardNumber: authObject?.cardNumber, // 결제카드 번호
    planAmount: authObject?.planAmount, // 결제 금액

    usableStatus: authObject?.usableStatus,

    paymentDueDate: authObject?.paymentDueDate,

    /** 구독플랜 인원 정보 */
    planLimitCount: authObject?.planLimitCount, // 플랜 제한 이용 인원
    prevPlanLimitCount: authObject?.prevPlanLimitCount, // 구독 인원 변경시 변경전 플랜카운트
    changedPlanLimitCount: authObject?.changedPlanLimitCount, // 구독 인원 변경시 변경 플랜카운트

    confirmUsableStatus: true, // 경고창 관련 boolean
    testAccount: authObject?.testAccount,
});

const useUsableStatus = ({ oneButtonCallBack, twoButtonCallBack, authObject, setAuthObject, alertStateReset, enabled = true }: any) => {
    const navigate = useNavigate();

    /**
     * 경고창 버튼 콜백은 창이 뜬 뒤 한참 뒤에(계정이 바뀐 뒤에도) 불릴 수 있다 — 공통 알럿은 앞 콜백을 물려받기도 한다.
     * 창을 띄운 세션이 지금 세션일 때만 저장한다(lib/authSessionGuard). 아니면 아무것도 쓰지 않는다.
     */
    const alertCheck = (authSession: AuthOwner) => {
        if (!isAuthSessionCurrent(authSession)) return;
        const authObjectData = buildConfirmedUsableStatusAuthObject(authObject);
        setAuthObject(authObjectData);
        setItemWithExpiration('authObject', authObjectData, 12);
    };

    const navigateExpiredPage = (authSession: AuthOwner) => {
        alertCheck(authSession);
        navigate('/expired', { replace: true });
        alertStateReset();
    };

    const navigateMyPage = (authSession: AuthOwner) => {
        alertCheck(authSession);
        navigate('/myPage', {
            state: {
                isSubscriptionModal: true,
            },
            replace: true,
        });
    };

    useEffect(() => {
        if (!enabled) return;
        // 경고창을 띄우는 이 세션의 표식 — 버튼 콜백이 다른 세션에서 불리면 저장하지 않는다.
        const authSession = captureAuthSession(authObject);
        if (authObject) {
            if (!(authObject.usableStatus === 'needAction')) {
                // // 테스트용
                // if (authObject.usableStatus === 'trial') {
                //     oneButtonCallBack(
                //         '구독 종료',
                //         '주상화 매니저님! 7일 무료 이용 기간이 만료되었습니다!\n구독 후 이용 부탁드립니다',
                //         '구독 하기',
                //         () => {
                //             navigateExpiredPage();
                //         }
                //     );
                // }

                // 서비스 중단까지 남은 일수 (D-day). 음수면 0으로 클램프
                const dDay = Math.max(0, dayjs(authObject.expirationDelayDate).startOf('day').diff(dayjs().startOf('day'), 'day'));

                // 무료 요금제 결제 유예중
                if (authObject.usableStatus === 'trialPaymentPending' && authObject?.confirmUsableStatus === false) {
                    twoButtonCallBack(
                        '구독 결제 실패',
                        `${authObject.name} 매니저님, ${dayjs(authObject.expirationDate).format(
                            'MM월 DD일'
                        )}부로 무료체험이 만료됐어요.\n서비스 중단까지 D-${dDay} — 지금 바로 구독해 주세요.`,
                        true,
                        '다음에',
                        '지금 구독 갱신하기',
                        () => {
                            alertCheck(authSession);
                        },
                        () => {
                            navigateMyPage(authSession);
                        }
                    );
                }
                //  유료 요금제 결제 유예중
                if (authObject.usableStatus === 'planAborted' && authObject?.confirmUsableStatus === false) {
                    twoButtonCallBack(
                        '구독 결제 실패',
                        `${authObject.name} 매니저님, ${dayjs(authObject.finalPaymentTryDate).format(
                            'MM월 DD일'
                        )} 구독 결제가 실패했어요.\n서비스 중단까지 D-${dDay} — 지금 바로 갱신해 주세요.`,
                        true,
                        '다음에',
                        '지금 구독 갱신하기',
                        () => {
                            alertCheck(authSession);
                        },
                        () => {
                            navigateMyPage(authSession);
                        }
                    );
                }
                // 무료 요금제 만료 - needPlanToFree
                if (authObject.usableStatus === 'needPlanToFree' && authObject?.confirmUsableStatus === false) {
                    oneButtonCallBack(
                        '구독 종료',
                        `${authObject.name} 매니저님! 무료 이용 기간이 만료되었습니다!\n구독 후 이용 부탁드립니다`,
                        '구독 하기',
                        () => {
                            // navigateExpiredPage();
                            alertCheck(authSession);
                        }
                    );
                }
                // // 유료 요금제 만료 - needPlanToCharge
                if (authObject.usableStatus === 'needPlanToCharge' && authObject?.confirmUsableStatus === false) {
                    oneButtonCallBack(
                        '구독 종료',
                        `${authObject.name} 매니저님! 구독 이용 기간이 만료되었습니다!\n재구독 후 다시 이용하실 수 있습니다`,
                        '구독 하기',
                        () => {
                            // navigateExpiredPage();
                            alertCheck(authSession);
                        }
                    );
                }
            }
        }
    }, [authObject, enabled]);
};

export default useUsableStatus;
